import {
  describeEmailSourceAttachment,
  detectEmailSourceFormatFromName,
  EMAIL_SOURCE_MEDIA_TYPE_BY_FORMAT,
  type EmailSourceFormat,
  emailSourceFormatFromMediaType,
} from '@/lib/email-conversion/source-formats/email-source-format'

/**
 * Inline transport for an email source FILE: the composer reads the file in
 * the browser and carries it as a canonical `data:` URL file part, so the
 * source never touches the CDN. Isomorphic — the route decodes it with the
 * same helpers.
 */

type EmailSourceAttachmentPartLike = {
  filename?: string
  mediaType?: string
  url?: string
}

const DATA_URL = /^data:([^;,]*)((?:;[^;,]*)*),([\s\S]*)$/

const BASE64_CHUNK_BYTES = 32_768

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64')
  }
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_BYTES) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + BASE64_CHUNK_BYTES)
    )
  }
  return btoa(binary)
}

function base64ToBytes(payload: string): Uint8Array {
  const compact = payload.replace(/\s+/g, '')
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(compact, 'base64'))
  }
  const binary = atob(compact)
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

/** Canonical data URL: `data:<mediaType>;base64,<utf-8 base64>`. */
export function encodeEmailSourceDataUrl(
  format: EmailSourceFormat,
  text: string
): string {
  const bytes = new TextEncoder().encode(text)
  return `data:${EMAIL_SOURCE_MEDIA_TYPE_BY_FORMAT[format]};base64,${bytesToBase64(bytes)}`
}

function parseDataUrl(
  url: string | undefined
): { mediaType: string; isBase64: boolean; payload: string } | undefined {
  if (!url || !url.startsWith('data:')) {
    return
  }
  const match = url.match(DATA_URL)
  if (!match) {
    return
  }
  const [, mediaType = '', params = '', payload = ''] = match
  return {
    mediaType: mediaType.trim().toLowerCase(),
    isBase64: /;base64(?:;|$)/i.test(params),
    payload,
  }
}

/** True only for `data:` URLs whose media type identifies an email source. */
export function isEmailSourceDataUrl(url?: string): boolean {
  const parsed = parseDataUrl(url)
  return (
    parsed !== undefined &&
    emailSourceFormatFromMediaType(parsed.mediaType) !== undefined
  )
}

/** base64 or percent-encoded → `{ format, text }`; undefined otherwise. */
export function decodeEmailSourceDataUrl(
  url?: string
): { format: EmailSourceFormat; text: string } | undefined {
  const parsed = parseDataUrl(url)
  if (!parsed) {
    return
  }
  const format = emailSourceFormatFromMediaType(parsed.mediaType)
  if (!format) {
    return
  }
  try {
    const text = parsed.isBase64
      ? new TextDecoder().decode(base64ToBytes(parsed.payload))
      : decodeURIComponent(parsed.payload)
    return { format, text }
  } catch {
    // Malformed base64 / percent-encoding: not an email source.
  }
}

/**
 * Decoded byte length of a base64 data URL, computed from the payload
 * length alone (no decode). Undefined for percent-encoded payloads.
 */
export function emailSourceDataUrlByteLength(url: string): number | undefined {
  const parsed = parseDataUrl(url)
  if (!parsed?.isBase64) {
    return
  }
  const compact = parsed.payload.replace(/\s+/g, '')
  if (compact.length === 0) {
    return 0
  }
  let padding = 0
  if (compact.endsWith('==')) {
    padding = 2
  } else if (compact.endsWith('=')) {
    padding = 1
  }
  return Math.floor((compact.length * 3) / 4) - padding
}

/**
 * Filename/media type first (a hosted `.tsx` served as `text/plain` still
 * counts), then the data URL's own media type.
 */
export function getEmailSourceFormatForPart(
  part: EmailSourceAttachmentPartLike
): EmailSourceFormat | undefined {
  const named = detectEmailSourceFormatFromName(part.filename, part.mediaType)
  if (named) {
    return named
  }
  const parsed = parseDataUrl(part.url)
  return parsed ? emailSourceFormatFromMediaType(parsed.mediaType) : undefined
}

export function formatEmailSourceSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024
    return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Card description for an inline source: `HTML email source · 12 KB`. */
export function buildEmailSourcePreviewText(
  format: EmailSourceFormat,
  byteLength: number
): string {
  return `${describeEmailSourceAttachment(format)} · ${formatEmailSourceSize(byteLength)}`
}
